import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, X, FileUp, Download, Users } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { API_BASE } from "../DataContext";

const DOC_TYPES = ["Purchase Order", "PO Revision", "Debit Note", "Credit Note", "Other"];
const fmtDate = (iso) => (iso ? iso.slice(0, 10) : "—");
const kb = (n) => (n / 1024).toFixed(1) + " KB";

export function AdminDocuments({ token, onUnauthorized }) {
  const [docs, setDocs] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const auth = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    const [dr, vr] = await Promise.all([
      fetch(`${API_BASE}/api/admin/documents`, { headers: auth }),
      fetch(`${API_BASE}/api/admin/vendors`, { headers: auth }),
    ]);
    if (dr.status === 401) return onUnauthorized();
    setDocs(await dr.json()); setVendors(await vr.json());
  }, [token, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const del = async (d) => {
    if (!confirm(`Delete "${d.title}"?`)) return;
    const r = await fetch(`${API_BASE}/api/admin/documents/${d.id}`, { method: "DELETE", headers: auth });
    if (r.ok) load();
  };

  const upload = async (form) => {
    setUploading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v != null) fd.append(k, v); });
      const r = await fetch(`${API_BASE}/api/admin/documents`, { method: "POST", headers: auth, body: fd });
      if (!r.ok) throw new Error((await r.json()).detail || "Upload failed");
      setShowForm(false);
      await load();
    } catch (e) { alert(e.message); }
    finally { setUploading(false); }
  };

  return (
    <div data-testid="admin-documents">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">Upload documents (PDF, images) for vendors to download. Leave "Vendor" empty to share with all vendors.</p>
        <Button onClick={() => setShowForm(true)} data-testid="doc-new-btn" className="bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white rounded-full">
          <Plus className="h-4 w-4 mr-1" /> Upload Document
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">Title</th>
              <th className="px-4 py-3 font-semibold">Type</th>
              <th className="px-4 py-3 font-semibold">Vendor</th>
              <th className="px-4 py-3 font-semibold">Uploaded</th>
              <th className="px-4 py-3 font-semibold text-right">Size</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!docs && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            {docs && docs.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No documents yet.</td></tr>}
            {docs?.map((d) => {
              const vendor = vendors.find((v) => v.id === d.vendor_id);
              return (
                <tr key={d.id} className="border-t border-slate-100 hover:bg-sky-50/50">
                  <td className="px-4 py-3 font-semibold text-[#0a2a5e]">{d.title}<div className="text-xs text-slate-400 font-normal">{d.filename}</div></td>
                  <td className="px-4 py-3 text-slate-600">{d.doc_type}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {d.vendor_id ? (vendor?.name || "—") : <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full"><Users className="h-3 w-3" /> All vendors</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(d.created_at)}</td>
                  <td className="px-4 py-3 text-right text-slate-600 whitespace-nowrap">{kb(d.size || 0)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => del(d)} data-testid={`doc-del-${d.id}`} className="text-red-500 hover:text-red-700 p-1.5"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && <DocForm vendors={vendors} onCancel={() => setShowForm(false)} onSave={upload} saving={uploading} />}
    </div>
  );
}

function DocForm({ vendors, onCancel, onSave, saving }) {
  const [title, setTitle] = useState("");
  const [doc_type, setType] = useState("Purchase Order");
  const [vendor_id, setVendor] = useState("");
  const [file, setFile] = useState(null);

  const submit = (e) => {
    e.preventDefault();
    if (!title.trim() || !file) return alert("Title and file are required");
    onSave({ title, doc_type, vendor_id, file });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="doc-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-[#0a2a5e]">Upload Document</h2>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Title *</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="doc-title" placeholder="e.g. PO Revision — freight added" required />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Type</label>
              <select value={doc_type} onChange={(e) => setType(e.target.value)} data-testid="doc-type" className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm bg-white">
                {DOC_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Vendor (blank = all)</label>
              <select value={vendor_id} onChange={(e) => setVendor(e.target.value)} data-testid="doc-vendor" className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm bg-white">
                <option value="">All vendors</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">File *</label>
            <label htmlFor="doc-file" className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl px-4 py-6 cursor-pointer hover:border-[#0a2a5e] hover:bg-sky-50">
              <FileUp className="h-5 w-5 text-slate-400" />
              <span className="text-sm text-slate-600">{file ? file.name : "Click to select file"}</span>
            </label>
            <input id="doc-file" type="file" data-testid="doc-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onCancel} className="rounded-full">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="doc-save-btn" className="bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white rounded-full">
              {saving ? "Uploading…" : "Upload"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
